import React from "react";
import {
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  Paper,
  FormControl,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "../../../common/FormComponents/Tooltip";

type OtherInformationSectionProps = {
  otherInformation: {
    notes: string;
    chamberGroupInsurancePlanId: string;
    essoPlanId: string;
    customFields: Array<{
      id: string;
      label: string;
      value: string;
    }>;
  };
  onOtherInformationChange: ({
    field,
    value,
  }: {
    field: "notes" | "chamberGroupInsurancePlanId" | "essoPlanId";
    value: string;
  }) => void;
  onAddCustomField: () => void;
  onUpdateCustomField: ({
    id,
    field,
    value,
  }: {
    id: string;
    field: "label" | "value";
    value: string;
  }) => void;
  onRemoveCustomField: ({ id }: { id: string }) => void;
  errors: Record<string, string>;
};

const OtherInformationSection = ({
  otherInformation,
  onOtherInformationChange,
  onAddCustomField,
  onUpdateCustomField,
  onRemoveCustomField,
  errors,
}: OtherInformationSectionProps) => {
  const handleChange =
    (field: "notes" | "chamberGroupInsurancePlanId" | "essoPlanId") =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onOtherInformationChange({
        field,
        value: e.target.value,
      });
    };

  const handleCustomFieldChange =
    (id: string, field: "label" | "value") =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onUpdateCustomField({
        id,
        field,
        value: e.target.value,
      });
    };

  const handleAddCustomField = () => {
    onAddCustomField();
  };

  const handleRemoveCustomField = (id: string) => () => {
    onRemoveCustomField({ id });
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        mb: 4,
        borderRadius: "8px",
        backgroundColor: "#fcfcfc",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ fontWeight: "medium", color: "#333", mr: 1 }}
        >
          Other Information
        </Typography>
        <Tooltip content="Additional information and notes about the member" />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography
            variant="body1"
            component="h3"
            sx={{ fontWeight: "regular" }}
          >
            Notes
          </Typography>
          <Tooltip content="General notes about the member, preferences, or special requirements" />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "#d97706",
            mb: 2,
            fontWeight: "medium",
            backgroundColor: "#fef3c7",
            padding: "8px 12px",
            borderRadius: "4px",
            border: "1px solid #f59e0b",
          }}
        >
          ⚠️ Important: Do NOT add payment information (credit card numbers,
          bank details, etc.) to this field. Use the Payment Information section
          instead.
        </Typography>
        <FormControl fullWidth>
          <TextField
            multiline
            fullWidth
            rows={4}
            value={otherInformation.notes}
            onChange={handleChange("notes")}
            error={!!errors.notes}
            helperText={errors.notes}
            sx={{ mb: 3 }}
            placeholder="Add general notes about the member here (e.g., special requirements, preferences, etc.)"
            variant="outlined"
          />
        </FormControl>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography
            variant="body1"
            component="h3"
            sx={{ fontWeight: "medium" }}
          >
            Custom Fields
          </Typography>
          <Tooltip content="Add custom fields to store additional information about the member" />
        </Box>

        {otherInformation.customFields.map((field) => (
          <Box
            key={field.id}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              mb: 2,
              gap: 2,
            }}
          >
            <TextField
              placeholder="Field Name"
              value={field.label}
              onChange={handleCustomFieldChange(field.id, "label")}
              sx={{ flexGrow: 1 }}
              variant="outlined"
            />
            <TextField
              placeholder="Value"
              value={field.value}
              onChange={handleCustomFieldChange(field.id, "value")}
              sx={{ flexGrow: 1 }}
              variant="outlined"
            />
            <IconButton
              aria-label="delete field"
              onClick={handleRemoveCustomField(field.id)}
              sx={{ color: "error.main" }}
              tabIndex={0}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddCustomField}
        aria-label="Add custom field"
        tabIndex={0}
        sx={{
          mt: 1,
          borderColor: "#E1A731",
          color: "#E1A731",
          "&:hover": {
            borderColor: "#c78f1e",
            backgroundColor: "rgba(225, 167, 49, 0.04)",
          },
        }}
      >
        Add Custom Field
      </Button>
    </Paper>
  );
};

export default OtherInformationSection;
